import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Pill from './Pill';

/**
 * SelectedPills
 *
 * Renders a list of “pill” badges for the options that are currently selected.
 * If the number of selected items exceeds `maxVisible`, it collapses the remainder
 * behind a “+N more” toggle pill.
 *
 * @param {Object[]} options
 *   Array of all available options ({ id, name }).
 * @param {string[]} selectedIds
 *   Array of ids corresponding to currently selected options.
 * @param {number} maxVisible
 *   Maximum number of pills to display before collapsing extras.
 * @param {boolean} expanded
 *   Whether to show all selected pills (`true`) or only up to `maxVisible` (`false`).
 * @param {() => void} onToggleExpand
 *   Callback invoked when the “+N more” / “See less” pill is clicked.
 */
const SelectedPills = memo(({ options, selectedIds, maxVisible, expanded, onToggleExpand }) => {
  const selected = options.filter((o) => selectedIds.includes(o.id));
  if (!selected.length) return null;

  const visible = expanded ? selected : selected.slice(0, maxVisible);
  const hiddenCount = selected.length - maxVisible;

  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {visible.map((o) => (
        <Pill key={o.id} label={o.name} />
      ))}
      {hiddenCount > 0 && (
        <Pill
          label={expanded ? 'See less' : `+${hiddenCount} more`}
          onClick={onToggleExpand}
          isToggle
        />
      )}
    </div>
  );
});

SelectedPills.propTypes = {
  options: PropTypes.array.isRequired,
  selectedIds: PropTypes.array.isRequired,
  maxVisible: PropTypes.number.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
};

export default SelectedPills;
